import { datadogRum } from '@datadog/browser-rum-slim';
import { getDatadogTeamTag } from '@gonfalon/engineering-teams/datadog';
import type { Squad } from '@gonfalon/types/generated/Squad';

import { getSessionUrlWithTimestamp } from './internal/getSessionUrl';
import { nrNoticeError } from './internal/newRelicRum';
import { trackDogfoodError } from './internal/trackDogfoodError';
import type { RUMCustomErrorContext, TrackErrorOptions } from './types';

function hasApolloErrorInCauseChain(error: unknown): boolean {
  let current = error;
  while (current instanceof Error) {
    if (current.name === 'ApolloError') {
      return true;
    }
    current = current.cause;
  }
  return false;
}

export function trackError(error: Error, { isCrash, severity, url, owner, metadata }: TrackErrorOptions) {
  const inflightExperiences = window.__experienceTracker?.getState();

  if (severity === 'critical') {
    window.__experienceTracker?.endAll({ outcome: 'error', error: 'page_crash' });
  }

  const resolvedOwner = !owner && hasApolloErrorInCauseChain(error) ? 'team-o11y' : owner;
  const datadogTeamTag = resolvedOwner ? getDatadogTeamTag(resolvedOwner) : resolvedOwner;

  if (!error.message) {
    // oxlint-disable-next-line no-param-reassign
    error.message = `[${error.name}] (no message)`;
  }

  const context: RUMCustomErrorContext = {
    isCrash,
    severity,
    url,
    // TODO: until we remove "Squad" in favor of "EngineeringTeam", we need this cast
    // for those teams that haven't migrated to using "EngineeringTeam" the Squad string IS the Datadog team tag
    // once the migration is complete, we can have "owner" here be typed as the union of all EngineeringTeam['datadog']['teamKey'] entries
    owner: datadogTeamTag as Squad | undefined,
    metadata,
    inflightExperiences,
  };
  datadogRum.addError(error, context);
  // Datadog picks up the session replay URL in its `beforeSend` hook, which New
  // Relic never sees, so resolve it here for the New Relic payload. This
  // overrides the session-wide `sessionURL` attribute with a link seeked to the
  // moment of the error.
  const sessionURL = getSessionUrlWithTimestamp();
  // New Relic only accepts primitive custom-attribute values, so pass the flat
  // fields and serialize the rest.
  nrNoticeError(error, {
    ...(isCrash !== undefined ? { isCrash } : {}),
    ...(severity ? { severity } : {}),
    ...(url ? { url } : {}),
    ...(datadogTeamTag ? { owner: datadogTeamTag } : {}),
    ...(sessionURL ? { sessionURL } : {}),
    ...(metadata ? { metadata: safeStringify(metadata) } : {}),
  });
  trackDogfoodError(error, context);
}

// New Relic custom attributes must be primitives, so metadata is serialized.
// Guard against non-serializable values (e.g. circular structures) so a bad
// metadata object can't abort the rest of trackError (dogfood/LDObserve).
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable metadata]';
  }
}
